// web-commands.ts — the PER-APP half of the web shape: maps oriora_muster's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import { todayLocal } from "./store";
import type { Faction, Unit, Recipe, Paint } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const FACTIONS = "factions";
const UNITS = "units";
const RECIPES = "recipes";
const PAINTS = "paints";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function newRow(input: unknown): { id: string } & Record<string, unknown> {
  return { id: uuid(), created_at: nowIso(), updated_at: nowIso(), // strip explicitly-undefined keys: they would clobber the defaults above (the Stash
        // file-a-box crash pattern, fleet sweep 2026-08-19)
        ...Object.fromEntries(Object.entries(input as Record<string, unknown>).filter(([, v]) => v !== undefined)) };
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Factions (CRUD) ──
    list_factions: () => db.all(FACTIONS),
    create_faction: ({ input }) => db.insert(FACTIONS, newRow(input)),
    update_faction: ({ id, patch }) => {
      db.update(FACTIONS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_faction: ({ id }) => {
      db.remove(FACTIONS, String(id));
      return null;
    },

    // ── Units (CRUD, optional faction filter) ──
    list_units: ({ factionId }) => {
      const rows = db.all(UNITS);
      return factionId == null ? rows : rows.filter((r) => (r as { faction_id?: string }).faction_id === String(factionId));
    },
    get_unit: ({ id }) => db.get(UNITS, String(id)),
    // 🔴 `wip_since` is a DERIVED column the native side maintains in Rust (db.rs:434 on create,
    // db.rs:467-476 on update): entering WIP stamps today, staying in WIP keeps the original
    // stamp, leaving WIP clears it. The web shim never did any of that, so on the browser/web
    // surface it stayed null forever — and everything hanging off it silently died on web only:
    // the focus slot's "· sat Nd in progress", the card's stale-WIP flag ("sat Nd — finish or
    // move it", ≥30d), nextUpRanked's oldest-WIP-first ordering, and — worst — the `wip` slice of
    // the CollectionSignals payload (signals-web.ts:99-108 only fills wipItems when wip_since is
    // non-null), which is the figure AI-VALUE.md leads the billed recap with ("two units have
    // been WIP over a year"). Mirrored from the Rust below; the Rust stays the source of truth.
    create_unit: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      return db.insert(UNITS, { ...newRow(input), wip_since: i.status === "wip" ? todayLocal() : null });
    },
    update_unit: ({ id, patch }) => {
      const p = { ...(patch as Record<string, unknown>) };
      if (p.status !== undefined) {
        const prev = db.all(UNITS).find((r) => (r as { id?: string }).id === String(id)) as { status?: string; wip_since?: string | null } | undefined;
        p.wip_since = p.status === "wip"
          ? (prev?.status === "wip" ? prev?.wip_since ?? todayLocal() : todayLocal())
          : null;
      }
      db.update(UNITS, String(id), { ...p, updated_at: nowIso() });
      return null;
    },
    delete_unit: ({ id }) => {
      db.remove(UNITS, String(id));
      return null;
    },

    // ── Recipes (CRUD) ──
    list_recipes: () => db.all(RECIPES),
    create_recipe: ({ input }) => { const i = (input ?? {}) as Record<string, unknown>;
      return db.insert(RECIPES, { ...newRow(input), steps: i.steps ?? [] }); },
    update_recipe: ({ id, patch }) => {
      db.update(RECIPES, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_recipe: ({ id }) => {
      db.remove(RECIPES, String(id));
      return null;
    },

    // ── Paints (CRUD) ──
    list_paints: () => db.all(PAINTS),
    create_paint: ({ input }) => db.insert(PAINTS, newRow(input)),
    update_paint: ({ id, patch }) => {
      db.update(PAINTS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_paint: ({ id }) => {
      db.remove(PAINTS, String(id));
      return null;
    },

    // ── Derived — 1:1 JS port of signals.rs (signals-web.ts), fence verbatim.
    compute_signals: ({ today }) => {
      const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
      return computeSignalsWeb(
        db.all(FACTIONS) as unknown as Faction[],
        db.all(UNITS) as unknown as Unit[],
        db.all(RECIPES) as unknown as Recipe[],
        db.all(PAINTS) as unknown as Paint[],
        /^\d{4}-\d{2}-\d{2}/.test(String(today ?? "")) ? String(today).slice(0, 10) : localToday(),
      );
    },
  };
}
